import { itemIconKey } from '../registry/keys';

/** Placeable decoration definitions — pure data. Buildable terrain (path, fence)
 *  and growable plants (saplings) are modelled as decorations too. Some are
 *  `reward`-only (granted by goals, hidden from the shop). */
export interface DecorationDef {
  id: string;
  name: string;
  w: number;
  h: number;
  texture: string;
  icon: string;
  solid: boolean;
  ground: boolean;
  price: number;
  description: string;
  color: number;
  grows?: { stages: number; days: number; fruit: string };
  /** Only obtainable as a goal reward — hidden from the shop. */
  reward?: boolean;
}

const deco = (
  id: string, name: string, w: number, h: number, solid: boolean, ground: boolean,
  price: number, description: string, color: number,
  grows?: DecorationDef['grows'], reward?: boolean,
): DecorationDef => ({
  id, name, w, h, texture: `tx_deco_${id}`, icon: itemIconKey(id), solid, ground, price, description, color, grows, reward,
});

export const DECORATIONS: Record<string, DecorationDef> = {
  flowerpot: deco('flowerpot', 'Flower Pot', 1, 1, false, false, 80, 'A little pot of cheerful blooms.', 0xe06d8b),
  lantern: deco('lantern', 'Lantern', 1, 1, false, false, 120, 'A warm glow for the path.', 0xf2c14e),
  bench: deco('bench', 'Bench', 2, 1, true, false, 200, 'Somewhere to sit a while.', 0xb07a4f),
  scarecrow: deco('scarecrow', 'Scarecrow', 1, 2, true, false, 150, 'Keeps the crows guessing.', 0xd9a066),
  rug: deco('rug', 'Cozy Rug', 2, 2, false, true, 160, 'A soft 2×2 rug for the yard.', 0x9a5ea1),
  pinwheel: deco('pinwheel', 'Pinwheel', 1, 1, false, false, 90, 'Spins in the breeze.', 0x5ab0d8),
  birdbath: deco('birdbath', 'Bird Bath', 1, 1, true, false, 220, 'The birds adore it.', 0xbfd4dd),
  bush: deco('bush', 'Round Bush', 1, 1, true, false, 60, 'A tidy little shrub.', 0x4f9d44),
  fence: deco('fence', 'Fence', 1, 1, true, false, 40, 'Build a fence rail. Line them up!', 0x9a6b3f),
  path: deco('path', 'Path', 1, 1, false, true, 30, 'Lay a tidy path tile.', 0xcda878),
  water_tile: deco('water_tile', 'Water', 1, 1, true, true, 20, 'Build a river or pond — lay them in a row!', 0x4a90c2),
  bridge: deco('bridge', 'Bridge', 1, 1, false, true, 40, 'Cross a river — lay it over water.', 0xb07a4f),
  pet_bed: deco('pet_bed', 'Pet Bed', 1, 1, false, false, 80, 'A cozy bed for your pet.', 0xb07a4f),
  bee_hive: deco('bee_hive', 'Bee Hive', 1, 1, true, false, 350, 'Makes honey each morning.', 0xf2c14e),
  apple_sapling: deco('apple_sapling', 'Apple Tree', 1, 1, true, false, 150,
    'Plant it; it grows over a few days, then fruits each morning.', 0x9a6b3f, { stages: 3, days: 3, fruit: 'apple' }),
  sprinkler: deco('sprinkler', 'Sprinkler', 1, 1, false, false, 250, 'Waters the crops around it every morning.', 0x6fb0c8),
  copper_sprinkler: deco('copper_sprinkler', 'Copper Sprinkler', 1, 1, false, false, 0, 'Waters a 5×5 each morning — craft from copper.', 0xc97f4a, undefined, true),
  iron_sprinkler: deco('iron_sprinkler', 'Iron Sprinkler', 1, 1, false, false, 0, 'Waters a 7×7 each morning — craft from iron.', 0xb8b0a8, undefined, true),

  // ---- exclusive (goal rewards only — not in the shop) ----
  gnome: deco('gnome', 'Garden Gnome', 1, 1, false, false, 0, 'A cheerful gnome. Earned, not bought.', 0xd0584a, undefined, true),
  trophy: deco('trophy', 'Trophy', 1, 1, false, false, 0, 'Proof of a job well done.', 0xf2c14e, undefined, true),
  wind_chime: deco('wind_chime', 'Wind Chime', 1, 1, false, false, 0, 'Tinkles in the breeze.', 0xbfd4dd, undefined, true),
  star_banner: deco('star_banner', 'Star Banner', 1, 1, false, false, 0, 'A festive little banner.', 0xe0b84a, undefined, true),
  golden_statue: deco('golden_statue', 'Golden Statue', 1, 1, true, false, 0, 'It shines in the sun.', 0xf2c14e, undefined, true),
  fairy_lights: deco('fairy_lights', 'Fairy Lights', 1, 1, false, false, 0, 'Twinkly and warm.', 0x9fd0e6, undefined, true),
  weathervane: deco('weathervane', 'Weathervane', 1, 1, false, false, 0, 'Points to the breeze.', 0x9aa0a8, undefined, true),
  fountain: deco('fountain', 'Fountain', 1, 1, true, false, 0, 'A bubbling garden fountain.', 0x6fb0c8, undefined, true),
  crystal: deco('crystal', 'Crystal', 1, 1, false, false, 0, 'A sparkling crystal — craft from a gem.', 0x5ad0e0, undefined, true),
  // seasonal/holiday décor — only sold in their season
  pumpkin_deco: deco('pumpkin_deco', 'Jack-o-Lantern', 1, 1, true, false, 0, 'A festive autumn pumpkin.', 0xe08020, undefined, true),
  snowman: deco('snowman', 'Snowman', 1, 1, true, false, 0, 'A cheery winter snowman.', 0xeaf2f6, undefined, true),
  // ---- indoor furniture (real Sprout Lands sprites; file under Pip's "Home" dept) ----
  painting: deco('painting', 'Painting', 1, 1, false, false, 90, 'A framed landscape for the wall.', 0x7a9ac0),
  table_lamp: deco('table_lamp', 'Lamp', 1, 1, false, false, 110, 'A cozy lamp for indoors.', 0x9ac96a),
  houseplant: deco('houseplant', 'Potted Plant', 1, 1, false, false, 100, 'A leafy little houseplant.', 0x5a9d44),
  chair: deco('chair', 'Chair', 1, 1, false, false, 130, 'A comfy wooden chair.', 0xb07a4f),
  side_table: deco('side_table', 'Side Table', 1, 1, false, false, 120, 'A handy little table.', 0xb07a4f),
  wall_clock: deco('wall_clock', 'Clock', 1, 1, false, false, 150, 'Tick-tock! Keeps good time.', 0xc9a878),
  area_rug: deco('area_rug', 'Area Rug', 1, 1, false, true, 140, 'A soft patterned rug.', 0x5ab0d8),
  bed_blue: deco('bed_blue', 'Blue Bed', 1, 3, false, false, 220, 'A comfy bed with blue sheets.', 0x8ec8d8),
  bed_pink: deco('bed_pink', 'Pink Bed', 1, 3, false, false, 220, 'A comfy bed with pink sheets.', 0xe0a0b0),
  dresser: deco('dresser', 'Dresser', 1, 2, false, false, 180, 'A wooden chest of drawers.', 0xb07a4f),
};

export const getDecoration = (id: string): DecorationDef | undefined => DECORATIONS[id];
export const decorationList = (): DecorationDef[] => Object.values(DECORATIONS);
